/**
 * GUSTA CUSTOMIZATION: PREORDER + PICKUP ONLY
 *
 * Responsibilities:
 * - Prefer the first regular, purchasable variant over a preorder variant.
 * - Keep preorder, partial-preorder, and pickup-only line properties synchronized.
 * - Warn when the requested quantity exceeds currently available stock.
 * - Cap product quantities for tracked variants that do not allow overselling.
 * - Reconcile the saved item status after Warehouse cart-quantity changes.
 * - Initialize product forms loaded later in quick-view modals or sections.
 *
 * Inventory rules are rendered by snippets/gusta-preorder-product-data.liquid.
 * Do not place this code in Warehouse's compiled theme.js file.
 */
(function () {
  'use strict';

  var DATA_SELECTOR = 'script[data-gusta-preorder-variants]';
  var PROPERTY_SELECTOR = 'input[data-gusta-preorder-property]';
  var PICKUP_PROPERTY_SELECTOR = 'input[data-gusta-pickup-only-property]';
  var PICKUP_SELECTOR = '.product-info__block-item--store-pickup, .product-meta__store-availability-container';
  var PARTIAL_NOTICE_SELECTOR = '[data-gusta-partial-preorder-notice]';
  var PICKUP_NOTICE_SELECTOR = '[data-gusta-pickup-only-notice]';
  var STOCK_LIMIT_NOTICE_SELECTOR = '[data-gusta-stock-limit-notice]';
  var DYNAMIC_CHECKOUT_SELECTOR = '.shopify-payment-button';
  var CART_DATA_SELECTOR = 'script[data-gusta-cart-items]';
  var cartSyncPromise = null;
  var pendingCheckoutForm = null;

  function parseData(script) {
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('Gusta preorder: invalid variant data.', error);
      return null;
    }
  }

  function findVariant(data, variantId) {
    if (!data || !Array.isArray(data.variants)) {
      return null;
    }

    return data.variants.find(function (variant) {
      return String(variant.id) === String(variantId);
    }) || null;
  }

  function getCurrentVariant(form, data) {
    var variantIdInput = form.elements.namedItem('id');

    return findVariant(data, variantIdInput && variantIdInput.value);
  }

  function getRequestedQuantity(form) {
    var quantityInput = form && form.elements && form.elements.namedItem('quantity');
    var quantity = parseInt(quantityInput && quantityInput.value, 10);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function interpolateTranslation(template, token, value) {
    if (typeof template !== 'string') {
      return '';
    }

    return template.split(token).join(String(value));
  }

  function getItemStatus(variant, quantity) {
    if (!variant) {
      return '';
    }

    if (variant.preorder === true) {
      return 'PRE-ORDER';
    }

    if (
      variant.inventoryTracked === true &&
      variant.continueSelling === true &&
      variant.inventoryQuantity > 0 &&
      quantity > variant.inventoryQuantity
    ) {
      return 'PARTIAL PRE-ORDER';
    }

    return '';
  }

  function getStockLimit(variant) {
    if (
      !variant ||
      variant.inventoryTracked !== true ||
      variant.continueSelling === true ||
      variant.inventoryQuantity <= 0
    ) {
      return null;
    }

    var quantityRuleMax = parseInt(variant.quantityRuleMax, 10);

    return Number.isFinite(quantityRuleMax)
      ? Math.min(variant.inventoryQuantity, quantityRuleMax)
      : variant.inventoryQuantity;
  }

  function updateStockLimit(form, variant, translations) {
    var quantityInput = form && form.elements && form.elements.namedItem('quantity');

    if (!quantityInput || quantityInput.type === 'hidden') {
      return;
    }

    var stockLimit = getStockLimit(variant);
    var inventoryQuantity = parseInt(variant && variant.inventoryQuantity, 10);
    var showLowStockNotice =
      variant &&
      variant.inventoryTracked === true &&
      Number.isFinite(inventoryQuantity) &&
      inventoryQuantity > 0 &&
      inventoryQuantity <= 3;
    var quantityRuleMax = parseInt(variant && variant.quantityRuleMax, 10);
    var quantity = getRequestedQuantity(form);
    var step = parseInt(quantityInput.step, 10);
    var quantitySelector = quantityInput.closest('.quantity-selector');
    var increaseButton = quantitySelector && quantitySelector.querySelector('[data-action="increase-picker-quantity"]');
    var notice = form.querySelector(STOCK_LIMIT_NOTICE_SELECTOR);

    if (stockLimit !== null) {
      quantityInput.max = String(stockLimit);

      if (quantity > stockLimit) {
        quantityInput.value = String(stockLimit);
        quantity = stockLimit;
      }
    } else {
      if (Number.isFinite(quantityRuleMax)) {
        quantityInput.max = String(quantityRuleMax);
      } else {
        quantityInput.removeAttribute('max');
      }
    }

    if (showLowStockNotice && !notice) {
      notice = document.createElement('p');
      notice.className = 'gusta-stock-limit__notice';
      notice.dataset.gustaStockLimitNotice = 'true';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');

      var noticeContainer = quantityInput.closest('.product-form__quantity-with-rules') || quantityInput.parentElement;

      if (noticeContainer) {
        noticeContainer.appendChild(notice);
      }
    }

    if (notice) {
      if (showLowStockNotice) {
        var lowStockText = inventoryQuantity === 1
          ? translations.lowStockOne
          : interpolateTranslation(translations.lowStockMany, '__GUSTA_COUNT__', inventoryQuantity);

        if (notice.textContent !== lowStockText) {
          notice.textContent = lowStockText;
        }

        notice.hidden = false;
      } else {
        notice.hidden = true;
      }
    }

    if (increaseButton) {
      var increment = Number.isFinite(step) && step > 0 ? step : 1;
      var atStockLimit = stockLimit !== null && quantity + increment > stockLimit;

      if (!increaseButton.dataset.gustaIncreaseLabel) {
        increaseButton.dataset.gustaIncreaseLabel = increaseButton.getAttribute('aria-label') || translations.increaseQuantity;
      }

      if (!increaseButton.dataset.gustaIncreaseTitle) {
        increaseButton.dataset.gustaIncreaseTitle = increaseButton.getAttribute('title') || translations.increaseQuantity;
      }

      if (stockLimit !== null) {
        increaseButton.dataset.gustaStockLimitManaged = 'true';
        increaseButton.disabled = atStockLimit;
        increaseButton.classList.toggle('gusta-stock-limit__increase--disabled', atStockLimit);

        if (atStockLimit) {
          var stockLimitText = stockLimit === 1
            ? translations.stockLimitOne
            : interpolateTranslation(translations.stockLimitMany, '__GUSTA_COUNT__', stockLimit);

          increaseButton.setAttribute('aria-label', stockLimitText);
          increaseButton.setAttribute('title', stockLimitText);
        } else {
          increaseButton.setAttribute('aria-label', increaseButton.dataset.gustaIncreaseLabel);
          increaseButton.setAttribute('title', increaseButton.dataset.gustaIncreaseTitle);
        }
      } else if (increaseButton.dataset.gustaStockLimitManaged === 'true') {
        increaseButton.disabled = false;
        increaseButton.classList.remove('gusta-stock-limit__increase--disabled');
        increaseButton.setAttribute('aria-label', increaseButton.dataset.gustaIncreaseLabel);
        increaseButton.setAttribute('title', increaseButton.dataset.gustaIncreaseTitle);
        delete increaseButton.dataset.gustaStockLimitManaged;
      }
    }
  }

  function updateLineProperties(form, variant, itemStatus) {
    var preorderPropertyInput = form.querySelector(PROPERTY_SELECTOR);
    var pickupPropertyInput = form.querySelector(PICKUP_PROPERTY_SELECTOR);

    if (preorderPropertyInput) {
      // Warehouse may replace the buy-button markup while a variant changes.
      // Re-assert the value as well as the enabled state so the saved line
      // property survives that refresh and reaches the cart/order.
      preorderPropertyInput.value = itemStatus || 'PRE-ORDER';
      preorderPropertyInput.disabled = itemStatus === '';
    }

    if (pickupPropertyInput) {
      pickupPropertyInput.value = 'PICKUP ONLY';
      pickupPropertyInput.disabled = !(variant && variant.pickupOnly === true);
    }
  }

  function updatePartialPreorderNotice(form, variant, itemStatus, translations) {
    var partialNotice = form.querySelector(PARTIAL_NOTICE_SELECTOR);
    var pickupNotice = form.querySelector(PICKUP_NOTICE_SELECTOR);
    var isPartialPreorder = itemStatus === 'PARTIAL PRE-ORDER';

    if (partialNotice) {
      if (isPartialPreorder && variant) {
        var inventoryText = variant.inventoryQuantity === 1
          ? translations.partialInventoryOne
          : interpolateTranslation(translations.partialInventoryMany, '__GUSTA_COUNT__', variant.inventoryQuantity);

        var partialNoticeText = variant.pickupOnly === true
          ? interpolateTranslation(translations.partialPickup, '__GUSTA_INVENTORY__', inventoryText)
          : (inventoryText + ' ' + translations.partialAdditional).trim();

        if (partialNotice.textContent !== partialNoticeText) {
          partialNotice.textContent = partialNoticeText;
        }
      }

      partialNotice.hidden = !isPartialPreorder;
    }

    if (pickupNotice) {
      pickupNotice.hidden = isPartialPreorder;
    }

    form.querySelectorAll(DYNAMIC_CHECKOUT_SELECTOR).forEach(function (checkoutButton) {
      checkoutButton.classList.toggle('gusta-partial-preorder__dynamic-checkout--hidden', isPartialPreorder);
      checkoutButton.setAttribute('aria-hidden', isPartialPreorder ? 'true' : 'false');
    });
  }

  function updatePickupAvailability(form, variant) {
    var isPreorder = variant && variant.preorder === true;

    form.querySelectorAll(PICKUP_SELECTOR).forEach(function (pickupBlock) {
      pickupBlock.hidden = isPreorder;

      if (isPreorder) {
        pickupBlock.setAttribute('aria-hidden', 'true');
      } else {
        pickupBlock.removeAttribute('aria-hidden');
      }
    });
  }

  function synchronizeProductForm(form, variant, data) {
    var translations = data && data.translations ? data.translations : {};

    updateStockLimit(form, variant, translations);
    var itemStatus = getItemStatus(variant, getRequestedQuantity(form));

    updateLineProperties(form, variant, itemStatus);
    updatePickupAvailability(form, variant);
    updatePartialPreorderNotice(form, variant, itemStatus, translations);
  }

  function selectByFormControls(form, optionValueIds) {
    if (!Array.isArray(optionValueIds) || optionValueIds.some(function (value) { return !value; })) {
      return false;
    }

    var changedControl = null;

    optionValueIds.forEach(function (optionValueId, index) {
      var position = String(index + 1);
      var controls = Array.prototype.filter.call(form.elements, function (element) {
        return element.dataset && element.dataset.optionPosition === position;
      });

      var control = controls.find(function (element) {
        if (element.tagName === 'SELECT') {
          return Array.prototype.some.call(element.options, function (option) {
            return String(option.value) === String(optionValueId);
          });
        }

        return String(element.value) === String(optionValueId);
      });

      if (!control) {
        return;
      }

      if (control.tagName === 'SELECT') {
        control.value = String(optionValueId);
      } else {
        control.checked = true;
      }

      changedControl = control;
    });

    if (!changedControl) {
      return false;
    }

    changedControl.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function selectPreferredVariant(form, preferredVariant) {
    var picker = form.querySelector('variant-picker');

    if (!picker || !preferredVariant || !Array.isArray(preferredVariant.optionValueIds)) {
      return;
    }

    window.customElements.whenDefined('variant-picker').then(function () {
      if (typeof picker.selectCombination === 'function' && preferredVariant.optionValueIds.every(Boolean)) {
        picker.selectCombination({
          optionValues: preferredVariant.optionValueIds,
          productChange: false
        }).catch(function () {
          selectByFormControls(form, preferredVariant.optionValueIds);
        });
      } else {
        selectByFormControls(form, preferredVariant.optionValueIds);
      }
    });
  }

  function initializeScript(script) {
    var form = document.getElementById(script.dataset.formId) || script.closest('form');
    var data = parseData(script);

    if (!form || !data) {
      return;
    }

    var currentVariant = getCurrentVariant(form, data);
    var preferredVariant = findVariant(data, data.preferredVariantId);

    synchronizeProductForm(form, currentVariant, data);

    if (script.dataset.gustaPreorderInitialized === 'true') {
      return;
    }

    script.dataset.gustaPreorderInitialized = 'true';

    if (preferredVariant && preferredVariant.regular === true && (!currentVariant || currentVariant.regular !== true)) {
      selectPreferredVariant(form, preferredVariant);
    }
  }

  function initializeAll(root) {
    var scope = root && root.querySelectorAll ? root : document;

    if (scope.matches && scope.matches(DATA_SELECTOR)) {
      initializeScript(scope);
    }

    scope.querySelectorAll(DATA_SELECTOR).forEach(initializeScript);
  }

  document.addEventListener('variant:changed', function (event) {
    var form = event.target && event.target.matches && event.target.matches('form')
      ? event.target
      : document.getElementById(event.detail && event.detail.formId);

    if (!form) {
      return;
    }

    var script = form.querySelector(DATA_SELECTOR);
    var data = script && parseData(script);
    var variant = findVariant(data, event.detail && event.detail.variant && event.detail.variant.id);

    synchronizeProductForm(form, variant, data);
  });

  function synchronizeQuantityControl(control) {
    var form = control && control.closest && control.closest('form');
    var script = form && form.querySelector(DATA_SELECTOR);
    var data = script && parseData(script);

    if (form && data) {
      synchronizeProductForm(form, getCurrentVariant(form, data), data);
    }
  }

  document.addEventListener('input', function (event) {
    if (event.target && event.target.name === 'quantity') {
      synchronizeQuantityControl(event.target);
    }
  }, true);

  document.addEventListener('change', function (event) {
    if (event.target && event.target.name === 'quantity') {
      synchronizeQuantityControl(event.target);
    }
  }, true);

  document.addEventListener('click', function (event) {
    var quantityButton = event.target && event.target.closest && event.target.closest(
      '[data-action="increase-picker-quantity"], [data-action="decrease-picker-quantity"]'
    );

    if (quantityButton) {
      window.requestAnimationFrame(function () {
        synchronizeQuantityControl(quantityButton);
      });
    }
  }, true);

  // Re-assert the fields immediately before Warehouse builds the Ajax cart
  // request. This protects the order labels even if the theme has refreshed
  // the buy-button markup after our variant:changed listener ran.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    var script = form && form.querySelector && form.querySelector(DATA_SELECTOR);
    var data = script && parseData(script);

    if (form && data) {
      synchronizeProductForm(form, getCurrentVariant(form, data), data);
    }
  }, true);

  // FormData is also used directly by some Warehouse quick-add flows. Set or
  // remove the properties on the payload itself as a final synchronization.
  document.addEventListener('formdata', function (event) {
    var form = event.target;
    var script = form && form.querySelector && form.querySelector(DATA_SELECTOR);
    var data = script && parseData(script);
    var variant = form && data && getCurrentVariant(form, data);

    if (!form || !data || !event.formData) {
      return;
    }

    var itemStatus = getItemStatus(variant, getRequestedQuantity(form));

    if (itemStatus) {
      event.formData.set('properties[Item status]', itemStatus);
    } else {
      event.formData.delete('properties[Item status]');
    }

    if (variant && variant.pickupOnly === true) {
      event.formData.set('properties[Fulfillment]', 'PICKUP ONLY');
    } else {
      event.formData.delete('properties[Fulfillment]');
    }
  }, true);

  function getCartItemStatus(item) {
    if (!item) {
      return '';
    }

    // A full preorder designation remains attached once saved. This mirrors
    // the order-history rule and avoids silently losing the status if stock is
    // replenished while the item is still in the cart.
    if (item.itemStatus === 'PRE-ORDER') {
      return 'PRE-ORDER';
    }

    if (item.inventoryTracked === true && item.continueSelling === true) {
      if (item.inventoryQuantity <= 0) {
        return 'PRE-ORDER';
      }

      if (item.quantity > item.inventoryQuantity) {
        return 'PARTIAL PRE-ORDER';
      }
    }

    // Keep a previously saved partial-preorder status as a conservative
    // availability record. Shopify carts do not reserve inventory.
    return item.itemStatus === 'PARTIAL PRE-ORDER' ? 'PARTIAL PRE-ORDER' : '';
  }

  function findCartStatusMismatch() {
    var script = document.querySelector(CART_DATA_SELECTOR);
    var data = script && parseData(script);

    if (!data || !Array.isArray(data.items)) {
      return null;
    }

    for (var index = 0; index < data.items.length; index += 1) {
      var item = data.items[index];
      var itemStatus = getCartItemStatus(item);

      if (itemStatus && itemStatus !== item.itemStatus) {
        return {
          item: item,
          itemStatus: itemStatus
        };
      }
    }

    return null;
  }

  function refreshCartAfterStatusSync() {
    if (window.theme && window.theme.pageType === 'cart') {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { scrollToTop: false }
    }));
  }

  function synchronizeCartLineStatus(options) {
    var settings = options || {};
    var mismatch = findCartStatusMismatch();

    if (!mismatch) {
      return Promise.resolve(false);
    }

    if (cartSyncPromise) {
      return cartSyncPromise;
    }

    var properties = Object.assign({}, mismatch.item.properties || {});
    properties['Item status'] = mismatch.itemStatus;

    cartSyncPromise = fetch(window.routes.cartChangeUrl + '.js', {
      body: JSON.stringify({
        id: String(mismatch.item.key),
        quantity: mismatch.item.quantity,
        properties: properties
      }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Cart status update failed with HTTP ' + response.status + '.');
      }

      return response.json();
    }).then(function () {
      return true;
    }).catch(function (error) {
      console.warn('Gusta preorder: unable to synchronize cart item status.', error);
      return false;
    }).then(function (updated) {
      cartSyncPromise = null;

      if (pendingCheckoutForm) {
        var checkoutForm = pendingCheckoutForm;
        pendingCheckoutForm = null;
        resumeCheckout(checkoutForm);
      } else if (updated && settings.refresh !== false) {
        refreshCartAfterStatusSync();
      }

      return updated;
    });

    return cartSyncPromise;
  }

  function resumeCheckout(form) {
    var checkoutInput = form.querySelector('input[data-gusta-checkout-resume]');

    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'Checkout';
      checkoutInput.dataset.gustaCheckoutResume = 'true';
      form.appendChild(checkoutInput);
    }

    HTMLFormElement.prototype.submit.call(form);
  }

  document.addEventListener('submit', function (event) {
    var submitter = event.submitter;
    var form = event.target;

    if (!submitter || submitter.name !== 'checkout' || !form || !findCartStatusMismatch()) {
      return;
    }

    event.preventDefault();
    pendingCheckoutForm = form;
    synchronizeCartLineStatus({ refresh: false });
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    initializeAll(document);
    synchronizeCartLineStatus();

    var scheduled = false;
    var pendingScripts = new Set();
    var pendingCartSync = false;

    function addAffectedFormScript(node) {
      if (!(node instanceof Element)) {
        return;
      }

      if (node.matches(DATA_SELECTOR)) {
        pendingScripts.add(node);
      }

      node.querySelectorAll(DATA_SELECTOR).forEach(function (script) {
        pendingScripts.add(script);
      });

      var form = node.closest('form');
      var formScript = form && form.querySelector(DATA_SELECTOR);

      if (formScript) {
        pendingScripts.add(formScript);
      }

      node.querySelectorAll('form').forEach(function (nestedForm) {
        var nestedScript = nestedForm.querySelector(DATA_SELECTOR);

        if (nestedScript) {
          pendingScripts.add(nestedScript);
        }
      });
    }

    function affectsCartData(node) {
      if (!(node instanceof Element)) {
        return false;
      }

      return node.matches(CART_DATA_SELECTOR) ||
        Boolean(node.closest(CART_DATA_SELECTOR)) ||
        Boolean(node.querySelector(CART_DATA_SELECTOR));
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.target instanceof Element) {
          addAffectedFormScript(mutation.target);

          if (affectsCartData(mutation.target)) {
            pendingCartSync = true;
          }
        }

        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) {
            if (mutation.target instanceof Element && mutation.target.matches(CART_DATA_SELECTOR)) {
              pendingCartSync = true;
            }

            return;
          }

          addAffectedFormScript(node);

          if (affectsCartData(node)) {
            pendingCartSync = true;
          }
        });
      });

      if (pendingScripts.size === 0 && !pendingCartSync) {
        return;
      }

      if (scheduled) {
        return;
      }

      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;

        pendingScripts.forEach(function (script) {
          if (document.documentElement.contains(script)) {
            initializeScript(script);
          }
        });
        pendingScripts.clear();

        if (pendingCartSync) {
          pendingCartSync = false;
          synchronizeCartLineStatus();
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
})();
